import moment from "moment";
import "moment/locale/pl";
import i18n from "../i18n";

moment.locale("pl");

export const perPageOptions = [5, 10, 20, 50, 100, 200, 500, 1000];

export const passRegExp = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;
export const passRegExpLabel = "Min. 8 characters, at least one letter, one number and one special character";

export const removeByKey = (myObj, deleteKey) => {
  return Object.keys(myObj)
    .filter((key) => key !== deleteKey)
    .reduce((result, current) => {
      result[current] = myObj[current];
      return result;
    }, {});
};

export const statusElement = (status) => {
  switch (status) {
    case -1:
      return <span className="badge text-uppercase bg-primary-subtle text-primary fs-6"> {i18n.t('orders.info')} </span>;
    case 0:
      return <span className="badge text-uppercase bg-info-subtle text-info fs-6"> {i18n.t('orders.new')} </span>;
    case 1:
      return <span className="badge text-uppercase bg-secondary-subtle text-secondary fs-6"> {i18n.t('orders.accepted')} </span>;
    case 2:
      return <span className="badge text-uppercase bg-success-subtle text-success fs-6"> {i18n.t('orders.completed')} </span>;
    case 3:
      return <span className="badge text-uppercase bg-danger-subtle text-danger fs-6"> {i18n.t('orders.cancelled')} </span>;
    default:
      return <span className="badge text-uppercase bg-dark-subtle text-black fs-6"> {status} </span>;
  }
};

export const handleFullDate = (date) => {
  const dateR = moment(new Date(date)).format("DD MMM Y");
  const timeR = moment(new Date(date)).format("HH:mm");
  return `${dateR} ${timeR}`;
};

export const handleDate = (date) => {
  return moment(new Date(date)).format("DD MMM Y");
};

export const handleTime = (date) => {
  return moment(new Date(date)).format("HH:mm");
};

export const PAYMENT_OPTIONS = [
  {
    id: 1,
    value: "direct_transfer",
    label: "invoices.paymentTransfer",
    icon: "ri-bank-fill",
  },
  {
    id: 2,
    value: "cash_on_delivery",
    label: "invoices.paymentCOD",
    icon: "ri-money-dollar-box-fill",
  },
];

export const PRICE_DECIMALS = 2;

export const formatPriceValue = (value, decimals = PRICE_DECIMALS) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return Number(0).toFixed(decimals);
  }

  return numericValue.toFixed(decimals);
};

export const formatPrice = (value, currency = null, decimals = PRICE_DECIMALS) => {
  const formattedValue = formatPriceValue(value, decimals);
  return currency ? `${formattedValue} ${currency}` : formattedValue;
};

export const SUBACCOUNTS_PERMISSIONS = [
  {
    id: "shop",
    name: "shop",
    label: "subaccounts.permShop",
  },
  {
    id: "orders",
    name: "orders",
    label: "subaccounts.permOrders",
  },
  {
    id: "invoices",
    name: "invoices",
    label: "subaccounts.permInvoices",
  },
];

// Full set of permission names used app-wide (client subaccounts + sales/admin staff)
export const ALL_PERMISSIONS = [
  { name: "shop", label: "users.permGroups.permShop" },
  { name: "subaccounts", label: "users.permGroups.permSubaccounts" },
  { name: "orders", label: "users.permGroups.permOrders" },
  { name: "invoices", label: "users.permGroups.permInvoices" },
  { name: "sales-offers", label: "users.permGroups.permSalesOffers" },
  { name: "sales-offers-module", label: "users.permGroups.permSalesOffersModule" },
  { name: "sales-offers-manual-pricing", label: "users.permGroups.permSalesManualPricing" },
  { name: "all-orders", label: "users.permGroups.permAllOrders" },
  { name: "users", label: "users.permGroups.permUsers" },
  { name: "all-sales-offers", label: "users.permGroups.permAllSalesOffers" },
  { name: "download-centre", label: "users.permGroups.permDownloadCentre" },
];

export const PERMISSION_LABEL_KEYS = ALL_PERMISSIONS.reduce((keys, permission) => ({
  ...keys,
  [permission.name]: permission.label,
}), {});

export const invoiceStatusElement = (razem, kwota_zaplaty) => {
  if ((razem - kwota_zaplaty) === 0) {
    return <span className="badge text-uppercase bg-primary-subtle text-primary fs-6"> {i18n.t('invoices.statusPaid')} </span>;
  } else if ((razem - kwota_zaplaty) > 0 && (razem - kwota_zaplaty) < razem) {
    return <span className="badge text-uppercase bg-warning-subtle text-warning fs-6"> {i18n.t('invoices.statusPartiallyPaid')} </span>;
  } else if ((razem - kwota_zaplaty) === Number(razem)) {
    return <span className="badge text-uppercase bg-danger-subtle text-danger fs-6"> {i18n.t('invoices.statusUnpaid')} </span>;
  }

  return <span className="badge text-uppercase bg-primary-subtle text-primary fs-6"> {i18n.t('invoices.statusOther')} </span>;
};

const roundDiscountValue = (value) => parseFloat(formatPriceValue(value || 0));
const normalizeComparableId = (value) => String(value ?? "").trim();
const parseMotorFeatureLpList = (value) =>
  Array.isArray(value)
    ? value.map((item) => Number(item)).filter((item) => Number.isFinite(item))
    : [];

const resolveDiscountRow = (discounts, productId, priceGroup) => {
  const safeDiscounts = Array.isArray(discounts) ? discounts : [];
  const normalizedProductId = Number(productId);
  const normalizedGroup = normalizeComparableId(priceGroup);

  if (Number.isFinite(normalizedProductId) && normalizedProductId > 0) {
    const productDiscount = safeDiscounts.find(
      (item) => normalizeComparableId(item?.id_grupy_towarowej) === `-${normalizedProductId}`
    );
    if (productDiscount?.rabat != null) {
      return productDiscount;
    }
  }

  const groupDiscount = safeDiscounts.find(
    (item) => normalizeComparableId(item?.id_grupy_towarowej) === normalizedGroup
  );
  if (groupDiscount?.rabat != null) {
    return groupDiscount;
  }

  const genericDiscount = safeDiscounts.find(
    (item) => normalizeComparableId(item?.id_grupy_towarowej) === "0"
  );
  return genericDiscount?.rabat != null ? genericDiscount : null;
};

const sumMotorBreakdownAmount = (breakdown, motorFeatureLps = []) => {
  const lpSet = new Set(parseMotorFeatureLpList(motorFeatureLps).map((item) => String(item)));
  if (!lpSet.size || !Array.isArray(breakdown)) {
    return 0;
  }

  return breakdown.reduce((sum, item) => {
    const hasMotorCondition = Array.isArray(item?.conditions)
      && item.conditions.some((condition) => lpSet.has(String(condition?.lp ?? "")));
    if (!hasMotorCondition) {
      return sum;
    }
    return sum + Number(item?.amount || 0);
  }, 0);
};

export const calculatePrice = (price, price_group, discounts, options = {}) => {
  const retail_price = parseFloat(formatPriceValue(parseFloat(price) || 0));
  const selectedDiscountRow = resolveDiscountRow(discounts, options.productId, price_group);
  const discount = selectedDiscountRow?.rabat != null
    ? roundDiscountValue(selectedDiscountRow.rabat)
    : roundDiscountValue(options.fallbackDiscount || 0);
  const motorDiscountRow = (Array.isArray(discounts) ? discounts : []).find(
    (item) => normalizeComparableId(item?.id_grupy_towarowej) === "100"
  );
  const motorDiscount = motorDiscountRow?.rabat != null ? roundDiscountValue(motorDiscountRow.rabat) : null;
  const motorAmount = parseFloat(
    formatPriceValue(
      Math.max(
        0,
        options.motorAmount != null
          ? Number(options.motorAmount || 0)
          : sumMotorBreakdownAmount(options.breakdown, options.motorFeatureLps)
      )
    )
  );

  const appliedGroupId = normalizeComparableId(selectedDiscountRow?.id_grupy_towarowej);
  const motorFallbackDiscount = appliedGroupId === "0" ? discount : 0;

  let user_price = retail_price;
  if (motorAmount > 0) {
    const productBaseAmount = Math.max(0, retail_price - motorAmount);
    const discountedBase = productBaseAmount * ((100 - discount) / 100);
    const discountedMotor = motorAmount * ((100 - (motorDiscount ?? motorFallbackDiscount)) / 100);
    user_price = parseFloat(formatPriceValue(discountedBase + discountedMotor));
  } else if (discount) {
    user_price = parseFloat(formatPriceValue(retail_price * ((100 - discount) / 100)));
  }

  return {
    retail_price,
    user_price,
    discount,
    applied_discount_group: selectedDiscountRow?.id_grupy_towarowej ?? null,
    motor_discount: motorDiscount,
    motor_amount: motorAmount,
  };
};

export const getEffectiveCompoundDiscount = (retailPrice, userPrice) => {
  const retail = Number(retailPrice || 0);
  const user = Number(userPrice || 0);
  if (retail <= 0 || user < 0) return null;
  return Math.round((1 - user / retail) * 10000) / 100;
};

// Returns effective discount only when it meaningfully differs from stored discount
// (> 0.5% difference = compound discount from motor or similar)
export const getCompoundEffectiveDiscount = (retailPrice, userPrice, storedDiscount) => {
  const effective = getEffectiveCompoundDiscount(retailPrice, userPrice);
  if (effective == null) return null;
  const stored = Number(storedDiscount || 0);
  return Math.abs(effective - stored) > 0.5 ? effective : null;
};

export const getPriceCalculationInfo = (pricing = {}, priceGroup = "") => {
  const appliedGroup = String(pricing?.applied_discount_group ?? "").trim();
  const motorAmount = Number(pricing?.motor_amount || 0);
  const motorDiscount = pricing?.motor_discount != null ? Number(pricing.motor_discount) : null;
  const normalizedPriceGroup = String(priceGroup ?? "").trim();

  if (motorAmount > 0 && (motorDiscount != null || appliedGroup !== "0")) {
    const effective = getEffectiveCompoundDiscount(pricing?.retail_price, pricing?.user_price);
    const effectiveSuffix = effective != null
      ? ` ${i18n.t('cart.priceInfo.effectiveDiscount', { discount: formatPriceValue(effective) })}`
      : '';
    if (motorDiscount != null) {
      return i18n.t('cart.priceInfo.motorWithDiscount', { productDiscount: formatPriceValue(pricing?.discount || 0), motorDiscount: formatPriceValue(motorDiscount) }) + effectiveSuffix;
    }
    return i18n.t('cart.priceInfo.motorRetailPrice', { productDiscount: formatPriceValue(pricing?.discount || 0) }) + effectiveSuffix;
  }

  if (appliedGroup) {
    if (appliedGroup.startsWith("-")) {
      return i18n.t('cart.priceInfo.productDiscount', { discount: formatPriceValue(pricing?.discount || 0) });
    }
    if (appliedGroup === "0") {
      return i18n.t('cart.priceInfo.generalDiscount', { discount: formatPriceValue(pricing?.discount || 0) });
    }
    if (appliedGroup === normalizedPriceGroup) {
      return i18n.t('cart.priceInfo.groupDiscount', { group: normalizedPriceGroup, discount: formatPriceValue(pricing?.discount || 0) });
    }
  }

  if (Number(pricing?.discount || 0) > 0) {
    return i18n.t('cart.priceInfo.appliedDiscount', { discount: formatPriceValue(pricing.discount) });
  }

  return "";
};

export const slugify = (title) => {
  return title
    .trim()
    .replace(/ +/g, "-")
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "");
};
